import React, {ChangeEvent, FC, useContext} from "react";
import {T, useTranslate} from "@tolgee/react";
import AttachmentIcon from "@material-ui/icons/Attachment";
import {Box, Button} from "@material-ui/core";
import {BasicInput} from "view/components/Form";
import {attachmentFileFormats} from "constraints/general";
import {FullProduct, ProductNoteAttachment} from "ts/api/product";
import {buildNoteAttachment} from "view/components/ShoppingCart/noteAttachment";
import {LocalMapContext} from "view/context/LocalMapContext";
import {makeLowerCase} from "tools/show/localMap";
import {useDynamicInputHeight} from "hooks/ui/useDynamicInputHeight";
import {generateTestId} from "tools/cypress/generateTestId";
import {LocalMapType} from "@evennt-tech/api-v2-core";
import useStyles from "./styles";

type ProductNotesBlockProps = {
  product: FullProduct;
  onNoteChange: (productId: string, note: string) => void;
  onAttachmentModalOpen: (
    productId: string,
    firstAttachment?: ProductNoteAttachment
  ) => void;
  id: string;
};

const ProductNotesBlock: FC<ProductNotesBlockProps> = ({
  product,
  onNoteChange,
  onAttachmentModalOpen,
  id,
}) => {
  const localMap = useContext(LocalMapContext);
  const classes = useStyles();
  const {t} = useTranslate();
  const standLabel = makeLowerCase(localMap.stand, LocalMapType.Stand);
  const {inputRef, inputHeight, labelRef} = useDynamicInputHeight();

  const {notesPlaceholder, isExhibitorNotesRequired} = product;
  const noteLabelText =
    notesPlaceholder ||
    t("common.shopping_cart.notes_placeholder", {stand: standLabel});

  const onAddFirstAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";

    if (!file) {
      return;
    }

    const attachment = await buildNoteAttachment(file);

    if (attachment) {
      onAttachmentModalOpen(product.id, attachment);
    }
  };

  const renderAttachmentButton = () => {
    const inputId = `product-attachment-${product.id}`;
    const areProductAttachmentsExist = !!product.notes?.attachments?.length;
    if (!areProductAttachmentsExist) {
      return (
        <>
          <input
            accept={attachmentFileFormats}
            id={inputId}
            type="file"
            multiple={false}
            className={classes.attachmentInput}
            onChange={onAddFirstAttachment}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor={inputId}>
            <Button
              component="span"
              className={classes.emptyAttachmentsButton}
              endIcon={<AttachmentIcon />}
            >
              <T keyName="common.shopping_cart.attach_files" />
            </Button>
          </label>
        </>
      );
    }
    return (
      <Button
        color="primary"
        endIcon={<AttachmentIcon />}
        onClick={() => onAttachmentModalOpen(product.id)}
      >
        <T keyName="common.shopping_cart.attach_files" />
      </Button>
    );
  };

  return (
    <Box className={classes.productNotesContainer}>
      <Box width="64%" maxWidth="64%" {...generateTestId(id, "notes", true)}>
        <BasicInput
          style={{marginBottom: 0, maxWidth: "100%"}}
          label={noteLabelText}
          required={!!isExhibitorNotesRequired}
          fullWidth
          name={`${product.id}.note`}
          onChange={(e) => onNoteChange(product.id, e.target.value)}
          InputProps={{
            className: classes.input,
            inputRef,
            style: {height: inputHeight || "auto"},
          }}
          InputLabelProps={{
            ref: labelRef,
            classes: {
              formControl: classes.labelFormControl,
            },
          }}
        />
      </Box>
      <Box mr={1}>{renderAttachmentButton()}</Box>
    </Box>
  );
};

export default ProductNotesBlock;
